import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, PopupListItem, Service } from 'webapp-types';
import { BillingFeature, PopupEvent, TeamSectionItemType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive, sortBy } from 'webapp/utils/common';

import makeArray from 'webapp/helpers/make-array';
import themeClasses from 'webapp/helpers/theme-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import TeamsTextComponent from 'webapp/components/teams/text';
import TeamsViewSectionGridComponent from 'webapp/components/teams/view/section/grid';
import TeamsViewSectionPopupEditComponent from 'webapp/components/teams/view/section/popup-edit';

import type { Board, BoardLoop, Report } from 'webapp/models-custom';
import TeamSection from 'webapp/models-custom/team-section';

import type { TeamSectionItemSnapshot } from 'webapp/services/model-manager/team-sections';

interface PlaceholderItem {
	type: string;
	icon: IconName;
	colorClass: string;
	imagePath: string;
	isDisabled: boolean;
	onClick: () => void;
}

export interface Args {
	model: TeamSection;
	isPreview: boolean;
	isPlaceholder: boolean;
	moveUp: (section: TeamSection) => void;
	moveDown: (section: TeamSection) => void;
	isFirst?: boolean;
	isLast?: boolean;
}

type Action =
	'addMetric' | 'addDataboard' | 'addReport' | 'moveUp' | 'moveDown' | 'editDetails' | 'delete';

type Option = PopupListItem<Action>;

export default class TeamsViewSectionIndexComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/team-section-items')
	declare mmTeamSectionItemsService: Service.ModelManager.TeamSectionItems;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;

	@tracked isAddItemHeaderContextOpened = false;
	@tracked isOptionsContextOpened = false;
	@tracked isAddItemPlaceholderContextOpened = false;

	get placeholderItems(): PlaceholderItem[] {
		const isPreview = this.args.isPreview;
		return [
			{
				type: 'metric',
				icon: 'Metrics',
				colorClass: 'blue',
				imagePath: '/images/svg/teams/placeholder/metric.svg',
				isDisabled: isPreview,
				onClick: this.addMetric,
			},
			{
				type: 'databoard',
				icon: 'Databoards',
				colorClass: 'navy',
				imagePath: '/images/svg/teams/placeholder/databoard_report.svg',
				isDisabled: isPreview,
				onClick: this.addDataboard,
			},
			{
				type: 'report',
				icon: 'Reports',
				colorClass: 'purple',
				imagePath: '/images/svg/teams/placeholder/databoard_report.svg',
				isDisabled: isPreview,
				onClick: this.addReport,
			},
		];
	}

	get id() {
		return this.args.model.id;
	}

	get name() {
		return this.args.model.name;
	}

	get description() {
		return this.args.model.description;
	}

	@cached
	get items() {
		const items = this.args.model.items.filter((item) => {
			const { boardModel, boardLoopModel } = item;
			// filter out recently deleted boards and board loops
			if (boardModel) {
				return !boardModel.deleted;
			} else if (boardLoopModel) {
				return !boardLoopModel.deleted;
			}
			return true;
		});
		return sortBy(items, 'position');
	}

	get position() {
		return this.args.model.position;
	}

	get isEmpty() {
		return this.items.length === 0;
	}

	get isTheOnlySection() {
		const { isFirst, isLast } = this.args;
		return isFirst && isLast;
	}

	get editOptions() {
		const options: Option[] = [];

		if (!this.args.isFirst) {
			options.push({
				icon: 'ChevronUp',
				text: 'Move up',
				action: 'moveUp',
			});
		}

		if (!this.args.isLast) {
			options.push({
				icon: 'ChevronDown',
				text: 'Move down',
				action: 'moveDown',
			});
		}

		options.push({
			icon: 'Pencil',
			text: 'Edit details',
			action: 'editDetails',
		});

		if (!this.isTheOnlySection) {
			options.push({
				class: 'delete',
				icon: 'Trash',
				text: 'Delete section',
				action: 'delete',
			});
		}

		return options;
	}

	get addOptions() {
		const options: Option[] = [];

		options.push({
			icon: 'Metrics',
			text: 'Metric',
			action: 'addMetric',
		});

		options.push({
			icon: 'Databoards',
			text: 'Dashboard',
			action: 'addDataboard',
		});

		options.push({
			icon: 'Reports',
			text: 'Report',
			action: 'addReport',
		});

		return options;
	}

	get canEdit() {
		return !this.args.isPreview;
	}

	get canEditTitle() {
		return this.canEdit && !this.accountService.isDemoAccount;
	}

	get isReadOnly() {
		return this.args.isPreview || this.accountService.isDemoAccount;
	}

	get canAddItem() {
		return this.canEdit && !this.args.model.isFull;
	}

	get maxSelectable() {
		return TeamSection.maxItems - this.items.length;
	}

	get buttonTheme() {
		return this.canEdit ? 'blue' : 'grey';
	}

	get titleChangeTrackingObj() {
		return {
			event: TrackingEvent.TEAM_PAGE_EDITED,
			data: { action: ['section title'] },
		};
	}

	get isDescriptionShown() {
		return this.canEdit || this.description.length > 0;
	}

	openAddItemContextPopup(e: PointerEvent, type: 'Placeholder' | 'Header') {
		if (
			this.accountService.handleDemo() ||
			this[`isAddItem${type}ContextOpened`] ||
			!this.canAddItem
		) {
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>(
			'.teams-section__add-item',
		);
		const popup = this.popupService.createListPopup(attachTo);

		if (popup) {
			popup.open({
				itemsList: this.addOptions,
				doAction: (actionName?: string) => {
					this.doOptionsAction(actionName as Action);
				},
			});

			this[`isAddItem${type}ContextOpened`] = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this[`isAddItem${type}ContextOpened`] = false;
				}
			});
		}
	}

	@action
	doNothing(_text: string): void {
		return;
	}

	@action
	changeDescription(text: string): void {
		const section = this.args.model;
		if (!section) {
			return;
		}

		const cleanText = text.trim(),
			currentSavedText = section.description;

		if (cleanText !== currentSavedText) {
			this.analyticsService.event(TrackingEvent.TEAM_PAGE_EDITED, {
				action: ['section description'],
			});
			section.description = cleanText;
			void section.save();
		}
	}

	@action
	addMetric() {
		if (this.accountService.handleDemo() || !this.canEdit) {
			return;
		}

		const addNewMetric = (metricSetupData: DSSetup.MetricSetupData) => {
			void this.mmTeamSectionItemsService
				.preparePayloadForCreate(metricSetupData)
				.then((objectData) => {
					void this.mmTeamSectionsService.batchAddItems(this.args.model, [
						{
							objectType: TeamSectionItemType.METRIC,
							objectData,
						},
					]);
				});
		};

		this.mmTeamSectionsService.openChangeMetricPopup(this.args.model, addNewMetric, null);
	}

	@action
	addDataboard() {
		if (this.accountService.handleDemo() || !this.canEdit) {
			return;
		}

		this.openSelectItemPopup([
			...this.mmBoardsService.boardModels,
			...this.mmBoardLoopsService.boardLoopModels,
		]);
	}

	@action
	addReport() {
		if (this.accountService.handleDemo() || !this.canEdit) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, true)) {
			return;
		}

		this.openSelectItemPopup(this.mmReportsService.reportModels, true);
	}

	@action
	moveUp() {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.args.moveUp(this.args.model);
	}

	@action
	moveDown() {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.args.moveDown(this.args.model);
	}

	@action
	editDetails() {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.popupService
			.createModal(TeamsViewSectionPopupEditComponent, {
				popupClass: 'popup-type-teams-edit-section',
				allowStacked: true,
			})
			.open({
				model: this.args.model,
				type: TeamSectionItemType.GOAL,
			});
	}

	@action
	delete() {
		this.mmTeamSectionsService.delete(this.args.model);
	}

	@action
	openOptionsDropdown(e: PointerEvent) {
		if (this.isOptionsContextOpened || this.args.isPreview) {
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.teams-section__options');
		const popup = this.popupService.createListPopup(attachTo);

		if (popup) {
			popup.open({
				itemsList: this.editOptions,
				doAction: (actionName?: string) => {
					if (actionName) {
						this.doOptionsAction(actionName);
					}
				},
			});

			this.isOptionsContextOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isOptionsContextOpened = false;
				}
			});
		}
	}

	@action
	addItemFromPlaceholder(e: PointerEvent) {
		this.openAddItemContextPopup(e, 'Placeholder');
	}

	@action
	addItemFromDescription(e: PointerEvent) {
		this.openAddItemContextPopup(e, 'Header');
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	private openSelectItemPopup(
		allItems: (Board | BoardLoop | Report)[],
		isForSelectingReports = false,
	) {
		const popup = this.mmBoardsService.createBoardsSelectListModal();
		popup.open({
			model: allItems,
			maxSelectable: this.maxSelectable,
			isTeamSection: true,
			isForSelectingReports,
			onConfirm: (
				_selectedReportIds: number[],
				selectedItemObjects: TeamSectionItemSnapshot[],
			) => {
				return new Promise((resolve) => {
					void this.mmTeamSectionsService
						.batchAddItems(this.args.model, selectedItemObjects)
						.then(() => {
							resolve(true);
						});
				});
			},
		});
	}
	<template>
		<div
			class="teams-section
				{{unless this.isDescriptionShown 'teams-section--description-hidden'}}"
		>
			<div class="teams-section__header">
				<div class="teams-section__title">
					<EditableModelPropertyComponent
						@model={{@model}}
						@editableProperty="name"
						@canEdit={{this.canEditTitle}}
						@trackingObj={{this.titleChangeTrackingObj}}
					/>
				</div>
				{{#if this.canEdit}}
					<div class="teams-section__actions">
						<div
							role="button"
							class="teams-section__add-item
								{{unless this.canAddItem 'teams-section__add-item--disabled'}}
								{{if
									this.isAddItemHeaderContextOpened
									'teams-section__add-item--active'
								}}"
							{{on "click" this.addItemFromDescription}}
						>
							<CommonUiIconComponent @name="Plus" @size="sm" />
							Add item
						</div>
						{{#unless @isPlaceholder}}
							<div
								role="button"
								class="teams-section__options
									{{if @isPreview 'teams-section__options--disabled'}}
									{{if
										this.isOptionsContextOpened
										'teams-section__options--active'
									}}"
								{{on "click" this.openOptionsDropdown}}
							>
								<CommonUiIconComponent @name="Ellipsis" @size="sm" />
							</div>
						{{/unless}}
					</div>
				{{/if}}
			</div>
			{{#if @isPlaceholder}}
				<div class="teams-section__placeholder-items">
					{{#each this.placeholderItems as |item|}}
						<div
							role="button"
							class="teams-section__placeholder-item
								{{if item.isDisabled 'teams-section__placeholder-item--disabled'}}"
							{{on "click" item.onClick}}
						>
							<img src={{item.imagePath}} alt="{{item.type}} placeholder" />
							<div class="teams-section__placeholder-item-overlay">
								<CommonUiIconComponent
									@name={{item.icon}}
									@size="xl"
									@class="icon--{{item.colorClass}}"
								/>
							</div>
						</div>
					{{/each}}
				</div>
				<div class="teams-section__placeholder-info">
					<div class="teams-section__placeholder-title">
						Add sections to organize items by departments, channels, or initiatives.
					</div>

					<div
						role="button"
						class="button {{themeClasses this.buttonTheme}} teams-section__add-item"
						{{on "click" this.addItemFromPlaceholder}}
					>
						<CommonUiIconComponent @name="Plus" @size="sm" />Add item
					</div>
				</div>
			{{else}}
				{{#if this.isDescriptionShown}}
					<div
						class="teams-section__description teams-html-editor
							{{if this.isReadOnly 'teams-html-editor--readonly'}}"
					>
						<TeamsTextComponent
							@placeholder="This is a customizable section. Use it to share items to your team's needs. Click to change the description..."
							@text={{this.description}}
							@onChange={{this.doNothing}}
							@onFocusOut={{this.changeDescription}}
							@isPreview={{this.isReadOnly}}
						/>
					</div>
				{{/if}}
				<div class="teams-section__items">
					{{#if this.isEmpty}}
						<ul class="flex-ct flex-ct--size-team-section-item teams-section__grid">
							<li class="flex-item teams-section__empty-item">
								<div class="teams-section__empty-item-overlay">
									{{#if this.canEdit}}
										<div
											role="button"
											class="button
												{{themeClasses this.buttonTheme}}
												teams-section__add-item"
											{{on "click" this.addItemFromPlaceholder}}
										>
											<CommonUiIconComponent @name="Plus" @size="sm" />Add
											item
										</div>
									{{/if}}
								</div>
							</li>

							{{! Fake items for flex alignment }}
							{{#each (makeArray length=21)}}
								<li class="flex-item flex-item--fake"></li>
							{{/each}}
						</ul>
					{{else}}
						<TeamsViewSectionGridComponent @section={{@model}} @items={{this.items}} />
					{{/if}}
				</div>
			{{/if}}
		</div>
	</template>
}
